(() => {
  if (window.__sizeLockInitialized) return;
  window.__sizeLockInitialized = true;

  const VARIANT_SELECTS_SELECTOR = 'variant-selects[id^="variant-selects-"], variant-selects[data-section]';
  const SIZE_HANDLES = new Set(['size', 'groesse', 'grosse']);
  const ROOT_LOCK_CLASS = 'size-lock-active';
  const sectionState = new Map();
  const settleJobs = new Map();

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const escapeSelector = (value) => {
    const text = String(value || '');
    if (window.CSS?.escape) return CSS.escape(text);
    return text.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const getSectionId = (variantSelects) => {
    if (!variantSelects) return null;
    if (variantSelects.dataset.section) return variantSelects.dataset.section;
    if (variantSelects.id?.startsWith('variant-selects-')) {
      return variantSelects.id.replace('variant-selects-', '');
    }
    return null;
  };

  const getState = (sectionId) => {
    if (!sectionState.has(sectionId)) {
      sectionState.set(sectionId, { sizeTouched: false, locked: true });
    }
    return sectionState.get(sectionId);
  };

  const syncRootClass = () => {
    const locked = Array.from(sectionState.values()).some((state) => state.locked && !state.sizeTouched);
    document.documentElement.classList.toggle(ROOT_LOCK_CLASS, locked);
  };

  const findVariantSelects = (sectionId, scope = document) => {
    if (!sectionId) return null;
    const escaped = escapeSelector(sectionId);
    return (
      scope.querySelector?.(`#variant-selects-${escaped}`) ||
      scope.querySelector?.(`variant-selects[data-section="${escaped}"]`) ||
      document.querySelector(`#variant-selects-${escaped}`) ||
      document.querySelector(`variant-selects[data-section="${escaped}"]`)
    );
  };

  const findSizeFieldset = (variantSelects) => {
    if (!variantSelects) return null;
    return (
      Array.from(variantSelects.querySelectorAll('.product-form__input[data-option-handle]')).find((fieldset) =>
        SIZE_HANDLES.has(fieldset.dataset.optionHandle)
      ) || null
    );
  };

  const isSizeChosen = (fieldset) => Boolean(fieldset?.querySelector('input[type="radio"]:checked'));

  const clearAutomaticSize = (fieldset) => {
    if (!fieldset) return;
    fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = false;
      radio.removeAttribute('checked');
    });
  };


  // --- Third-party picker adapter (Globo Color Swatch) ---------------------
  // This store's PDP hides Dawn's <variant-selects> and shows Globo's own
  // radios instead. Globo mirrors its clicks into Dawn's radios (which is what
  // the lock listens to), but it does not mirror our "cleared" state back, so
  // while locked we also uncheck Globo's size radios and blank its
  // "Size: S" caption. Everything else (submit guard, state) stays on Dawn.
  const normalizeHandle = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const findGloboSizeGroup = (sectionId, origin = null) => {
    const root =
      origin?.closest?.('product-info') ||
      findVariantSelects(sectionId)?.closest('product-info') ||
      document;
    return (
      Array.from(root.querySelectorAll('.globo-swatch-product-detail .swatch--gl')).find((group) => {
        const legend = group.querySelector('legend.name-option');
        const name = legend?.dataset.name || legend?.firstChild?.textContent || '';
        return SIZE_HANDLES.has(normalizeHandle(name));
      }) || null
    );
  };

  const clearThirdPartySize = (sectionId, origin = null) => {
    const group = findGloboSizeGroup(sectionId, origin);
    if (!group) return;
    group.dataset.sizeLockGroup = 'true';
    group.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = false;
      radio.removeAttribute('checked');
    });
    const caption = group.querySelector('legend .name-variant');
    if (caption) {
      Array.from(caption.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) node.textContent = '';
      });
    }
  };

  const getProductForm = (sectionId, origin = null) =>
    origin?.closest?.('product-form') ||
    document.querySelector(`product-form[data-section-id="${escapeSelector(sectionId)}"]`);

  const getCartForm = (productForm) =>
    productForm?.querySelector('form[data-type="add-to-cart-form"]') ||
    productForm?.querySelector('form[action*="/cart/add"]') ||
    null;

  const getSubmitButton = (productForm) => {
    const form = getCartForm(productForm);
    return form?.querySelector('button[name="add"][type="submit"]') || form?.querySelector('.product-form__submit');
  };

  const getButtonLabel = (button) => button?.querySelector('span') || button;

  const getCopy = (productForm) => ({
    label: productForm?.dataset.sizeLockLabel || 'SELECT A SIZE',
    error: productForm?.dataset.sizeLockError || 'Please select a size',
  });

  // ---- Locked price: while no size is chosen, show the price of the smallest size of the
  // ---- current colour (S for adult, XS for youth = first variant of that colour), never the
  // ---- SKU from the ad / URL. Dawn re-renders the price on variant change; we re-apply after.
  const variantMaps = new Map();
  const getVariantMap = (productForm) => {
    if (!productForm) return [];
    if (variantMaps.has(productForm)) return variantMaps.get(productForm);
    let list = [];
    try { list = JSON.parse(productForm.querySelector('script[data-size-lock-variants]')?.textContent || '[]'); } catch (e) {}
    variantMaps.set(productForm, list);
    return list;
  };
  const formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const n = (Number(cents) || 0) / 100;
    const withSep = (v, thousands, decimal, decimals) => {
      const parts = v.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withSep(n, ',', '.', 0);
        case 'amount_with_comma_separator': return withSep(n, '.', ',', 2);
        case 'amount_no_decimals_with_comma_separator': return withSep(n, '.', ',', 0);
        case 'amount_with_apostrophe_separator': return withSep(n, "'", '.', 2);
        default: return withSep(n, ',', '.', 2);
      }
    });
  };
  const currentColour = (sectionId) => {
    const vs = findVariantSelects(sectionId);
    const colourFieldset = vs && Array.from(vs.querySelectorAll('.product-form__input[data-option-handle]')).find((f) => !SIZE_HANDLES.has(f.dataset.optionHandle));
    const checked = colourFieldset?.querySelector('input[type="radio"]:checked') || colourFieldset?.querySelector('select');
    return checked ? checked.value : null;
  };
  let priceWriting = false;
  const applyLockedPrice = (sectionId) => {
    const productForm = getProductForm(sectionId);
    const state = getState(sectionId);
    const ready = () => document.documentElement.classList.add('size-lock-price-ready');
    if (!productForm || state.sizeTouched) { ready(); return; }
    const list = getVariantMap(productForm);
    const colour = currentColour(sectionId);
    const v = list.find((x) => colour ? x.o1 === colour : true) || list[0];
    const priceEl = document.querySelector(`#price-${escapeSelector(sectionId)} .price`) || productForm.closest('.product__info-container')?.querySelector('.price');
    if (!v || !priceEl) { ready(); return; }
    const fmt = productForm.dataset.moneyFormat;
    const onSale = v.cap && v.cap > v.price;
    const reg = priceEl.querySelector('.price__regular .price-item--regular');
    const cmp = priceEl.querySelector('.price__sale s.price-item--regular');
    const sale = priceEl.querySelector('.price__sale .price-item--sale');
    const want = { price: formatMoney(v.price, fmt), cap: onSale ? formatMoney(v.cap, fmt) : '' };
    const set = (el, text) => { if (el && el.textContent.trim() !== text) el.textContent = text; };
    // Only touch the DOM when something actually differs (the MutationObserver below would
    // otherwise re-trigger this on our own writes).
    priceWriting = true;
    if (priceEl.classList.contains('price--on-sale') !== !!onSale) priceEl.classList.toggle('price--on-sale', !!onSale);
    if (priceEl.classList.contains('price--sold-out')) priceEl.classList.remove('price--sold-out');
    set(reg, want.price);
    set(cmp, want.cap);
    set(sale, want.price);
    priceWriting = false;
    ready();
  };
  // Re-apply if Dawn rewrites the price while still locked (its section re-render).
  let priceTimer = null;
  new MutationObserver((records) => {
    if (priceWriting) return;
    if (!records.some((r) => r.target.closest && r.target.closest('.product__info-container, product-info'))) return;
    clearTimeout(priceTimer);
    priceTimer = setTimeout(() => {
      sectionState.forEach((state, sectionId) => { if (!state.sizeTouched) applyLockedPrice(sectionId); });
    }, 30);
  }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });

  const applySoftLock = (sectionId, locked, origin = null) => {
    const productForm = getProductForm(sectionId, origin);
    if (!productForm) return;

    const button = getSubmitButton(productForm);
    const label = getButtonLabel(button);
    const copy = getCopy(productForm);

    productForm.classList.toggle('size-lock-soft', locked);
    productForm.dataset.sizeLockSoft = locked ? 'true' : 'false';

    if (!button || !label) return;

    if (locked) {
      if (!button.dataset.sizeLockOriginalText) {
        button.dataset.sizeLockOriginalText = (label.textContent || '').replace(/\s+/g, ' ').trim();
      }
      // Idle lock: button keeps its normal label ("Add to cart") and look.
      // Only a submit attempt without a size switches it to the nudge label
      // (see flagMissingSize).
      button.dataset.sizeLockOwned = 'true';
      return;
    }

    if (button.dataset.sizeLockOwned !== 'true') return;

    const originalText = button.dataset.sizeLockOriginalText || '';
    button.classList.remove('size-lock-button');
    button.removeAttribute('aria-disabled');
    delete button.dataset.sizeLockOwned;

    if ((label.textContent || '').replace(/\s+/g, ' ').trim() === copy.label && originalText) {
      label.textContent = originalText;
    }
  };

  const flagMissingSize = (productForm) => {
    const button = getSubmitButton(productForm);
    const label = getButtonLabel(button);
    if (!button || !label) return;
    if (!button.dataset.sizeLockOriginalText) {
      button.dataset.sizeLockOriginalText = (label.textContent || '').replace(/\s+/g, ' ').trim();
    }
    button.dataset.sizeLockOwned = 'true';
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('size-lock-button');
    label.textContent = getCopy(productForm).label;
  };

  const showError = (productForm) => {
    if (!productForm) return;
    flagMissingSize(productForm);
    const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
    const message = productForm.querySelector('.product-form__error-message');
    if (!wrapper || !message) return;
    message.textContent = getCopy(productForm).error;
    wrapper.hidden = false;
    wrapper.removeAttribute('hidden');
  };

  const hideError = (productForm) => {
    const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) return;
    wrapper.hidden = true;
    wrapper.setAttribute('hidden', 'hidden');
  };

  const cancelSettleJobs = (sectionId) => {
    const jobs = settleJobs.get(sectionId) || [];
    jobs.forEach((job) => {
      if (job.type === 'raf') cancelAnimationFrame(job.id);
      if (job.type === 'timeout') clearTimeout(job.id);
    });
    settleJobs.delete(sectionId);
  };

  const clearSizeForSection = (sectionId, scope = document) => {
    const state = getState(sectionId);
    if (state.sizeTouched) return;

    const variantSelects = findVariantSelects(sectionId, scope);
    const sizeFieldset = findSizeFieldset(variantSelects);
    if (!sizeFieldset) return;

    state.locked = true;
    clearAutomaticSize(sizeFieldset);
    clearThirdPartySize(sectionId, sizeFieldset);
    syncRootClass();
    applySoftLock(sectionId, true, sizeFieldset);
    applyLockedPrice(sectionId);
  };

  const schedulePostRenderClear = (sectionId, scope = document) => {
    cancelSettleJobs(sectionId);

    const jobs = [];
    clearSizeForSection(sectionId, scope);

    const rafId = requestAnimationFrame(() => clearSizeForSection(sectionId, scope));
    jobs.push({ type: 'raf', id: rafId });

    [60, 160, 360].forEach((delay) => {
      const id = setTimeout(() => clearSizeForSection(sectionId, scope), delay);
      jobs.push({ type: 'timeout', id });
    });

    settleJobs.set(sectionId, jobs);
  };

  const lockUntilSizeChoice = (sectionId, scope = document) => {
    if (!sectionId) return;
    const state = getState(sectionId);
    state.sizeTouched = false;
    state.locked = true;
    syncRootClass();
    applySoftLock(sectionId, true, scope instanceof Element ? scope : null);
    schedulePostRenderClear(sectionId, scope);
    hideError(getProductForm(sectionId));
  };

  const markSizeChosen = (sectionId, origin) => {
    const state = getState(sectionId);
    state.sizeTouched = true;
    state.locked = false;
    document.documentElement.classList.add('size-lock-price-ready');
    cancelSettleJobs(sectionId);
    syncRootClass();
    applySoftLock(sectionId, false, origin);
    hideError(getProductForm(sectionId, origin));
  };

  const releaseSectionWithoutSize = (sectionId, origin) => {
    cancelSettleJobs(sectionId);
    applySoftLock(sectionId, false, origin);
    document.documentElement.classList.add('size-lock-price-ready');
    sectionState.delete(sectionId);
    syncRootClass();
  };

  const initVariantSelects = (variantSelects, scope = document) => {
    const sectionId = getSectionId(variantSelects);
    if (!sectionId) return;

    const sizeFieldset = findSizeFieldset(variantSelects);
    if (!sizeFieldset) {
      releaseSectionWithoutSize(sectionId, variantSelects);
      return;
    }

    const state = getState(sectionId);
    if (state.sizeTouched && isSizeChosen(sizeFieldset)) {
      state.locked = false;
      syncRootClass();
      applySoftLock(sectionId, false, variantSelects);
      return;
    }

    lockUntilSizeChoice(sectionId, scope);
  };

  const initSizeLock = (scope = document) => {
    scope.querySelectorAll?.(VARIANT_SELECTS_SELECTOR).forEach((variantSelects) =>
      initVariantSelects(variantSelects, scope)
    );
  };

  const blockSubmitIfSizeMissing = (event, form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.dataset.type !== 'add-to-cart-form') return false;

    const productForm = form.closest('product-form');
    const sectionId = productForm?.dataset.sectionId;
    if (!sectionId) return false;

    const sizeFieldset = findSizeFieldset(findVariantSelects(sectionId));
    if (!sizeFieldset || isSizeChosen(sizeFieldset)) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    applySoftLock(sectionId, true, productForm);
    showError(productForm);
    sizeFieldset.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    return true;
  };

  /* ------------------------------------------------------------------------------------------
     Frequently Bought Together (Code Black Belt) guard — added 4 Nov 2026.
     The widget pre-ticks up to four products, pre-selects the first variant of each ("Colour / S")
     and its "Add selected to cart" button sits outside any <form>, so none of the guards above
     ever see it: one click added four size-S items and sent the customer to /cart.
     - Every bundled item's <select> gets a disabled "Select a size" placeholder. It is inserted
       silently (no change event) because the widget's own change handler throws on value "".
       The widget maps options by VALUE, not position (verified by a live add), so the extra
       option never shifts which variant is added.
     - The FIRST select ("this item") is the main product. The widget does not follow the
       customer's colour/size picks, so size-lock keeps that select equal to the main picker
       (syncFbtThisItem) and guards it by the main size picker instead of a placeholder.
     - The add button is blocked (capture phase, window level) while the main size is not chosen
       or any ticked bundled item still sits on its placeholder.
  ------------------------------------------------------------------------------------------ */
  const FBT_CONTAINER = '.cbb-frequently-bought-container';
  const FBT_SELECT = 'select.cbb-recommendations-variant-select';
  const FBT_THIS_ITEM = 'cbb-frequently-bought-this-item-variant-select';
  const FBT_BUTTON = '.cbb-frequently-bought-add-button';
  const FBT_PLACEHOLDER_TEXT = 'Select a size';

  const isFbtPlaceholder = (option) => Boolean(option && option.dataset.sizeLockPlaceholder === 'true');

  const installFbtPlaceholder = (select) => {
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.classList.contains(FBT_THIS_ITEM)) return; // synced by the widget to the main picker
    if (select.options.length < 2) return;                // nothing to choose
    if ([...select.options].some(isFbtPlaceholder)) return;
    const option = document.createElement('option');
    option.value = '';
    option.textContent = FBT_PLACEHOLDER_TEXT;
    option.disabled = true;
    option.dataset.sizeLockPlaceholder = 'true';
    select.insertBefore(option, select.firstChild);
    select.selectedIndex = 0; // silent: no change event, see note above
  };

  const initFbt = (scope = document) => {
    const root = scope instanceof Element ? scope : document;
    const containers = root.matches?.(FBT_CONTAINER) ? [root] : [...root.querySelectorAll(FBT_CONTAINER)];
    containers.forEach((container) => {
      container.querySelectorAll(FBT_SELECT).forEach(installFbtPlaceholder);
      const sectionId = mainSectionId(container);
      if (sectionId) syncFbtThisItem(sectionId);
    });
  };

  const fbtSelectFor = (checkbox, container) => {
    let node = checkbox.parentElement;
    while (node && node !== container) {
      const selects = node.querySelectorAll(FBT_SELECT);
      if (selects.length === 1) return selects[0];
      if (selects.length > 1) return null;
      node = node.parentElement;
    }
    return null;
  };

  const mainSectionId = (container) => {
    const productForm =
      container.closest('product-info')?.querySelector('product-form[data-section-id]') ||
      document.querySelector('product-info product-form[data-section-id]') ||
      document.querySelector('product-form[data-section-id]');
    return productForm?.dataset.sectionId || null;
  };

  const showFbtError = (container, text) => {
    const button = container.querySelector(FBT_BUTTON);
    if (!button) return;
    let message = container.querySelector('.size-lock-fbt-error');
    if (!message) {
      message = document.createElement('div');
      message.className = 'size-lock-fbt-error';
      message.setAttribute('role', 'alert');
      button.insertAdjacentElement('afterend', message);
    }
    message.textContent = text;
    message.hidden = false;
  };

  const hideFbtError = (container) => {
    const message = container.querySelector('.size-lock-fbt-error');
    if (message) message.hidden = true;
  };

  const fbtMissing = (container) => {
    const missingSelects = [];
    let mainSizeMissing = false;
    const sectionId = mainSectionId(container);
    const mainSizeFieldset = sectionId ? findSizeFieldset(findVariantSelects(sectionId)) : null;
    const productForm = sectionId ? getProductForm(sectionId) : null;

    container.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      if (!checkbox.checked) return;
      const select = fbtSelectFor(checkbox, container);
      if (!select) return;
      if (select.classList.contains(FBT_THIS_ITEM)) {
        if (mainSizeFieldset && !isSizeChosen(mainSizeFieldset)) {
          mainSizeMissing = true;
          missingSelects.push(select);
        }
        return;
      }
      if (isFbtPlaceholder(select.options[select.selectedIndex])) missingSelects.push(select);
    });

    // Outlines only ever mark what is still missing.
    container.querySelectorAll(`${FBT_SELECT}.size-lock-fbt-missing`).forEach((select) => {
      if (!missingSelects.includes(select)) select.classList.remove('size-lock-fbt-missing');
    });
    if (!missingSelects.length) hideFbtError(container);
    return { missingSelects, mainSizeMissing, sectionId, mainSizeFieldset, productForm };
  };

  const blockFbtIfSizeMissing = (event, button) => {
    const container = button.closest(FBT_CONTAINER);
    if (!container) return false;

    const { missingSelects, mainSizeMissing, sectionId, mainSizeFieldset, productForm } = fbtMissing(container);
    if (!missingSelects.length) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    missingSelects.forEach((select) => select.classList.add('size-lock-fbt-missing'));
    const copy = productForm ? getCopy(productForm) : null;
    showFbtError(container, copy?.error || 'Please select a size');

    if (mainSizeMissing && sectionId) {
      applySoftLock(sectionId, true, productForm);
      showError(productForm);
      mainSizeFieldset?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    } else {
      missingSelects[0].scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      missingSelects[0].focus?.({ preventScroll: true });
    }
    return true;
  };

  window.addEventListener(
    'click',
    (event) => {
      const button = event.target?.closest?.(FBT_BUTTON);
      if (!button) return;
      blockFbtIfSizeMissing(event, button);
    },
    true
  );

  // Re-evaluate as soon as the customer picks a size or (un)ticks an item, so the red outline and
  // the message disappear without another click. Only an error that is already showing is refreshed;
  // nothing is flagged before the customer has tried to add.
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const container = target.closest(FBT_CONTAINER);
      if (!container) return;
      if (!(target.matches(FBT_SELECT) || target.matches('input[type="checkbox"]'))) return;
      const message = container.querySelector('.size-lock-fbt-error');
      if (!message || message.hidden) return;
      fbtMissing(container);
    },
    true
  );

  // Keep the widget's "this item" dropdown equal to what the customer picked on the main product
  // (colour + size). Verified 4 Nov 2026: the widget does NOT follow Globo/Dawn picks on its own,
  // so without this it would add the main product in size S whatever the customer chose.
  const syncFbtThisItem = (sectionId) => {
    const variantSelects = findVariantSelects(sectionId);
    if (!variantSelects) return;
    const sizeFieldset = findSizeFieldset(variantSelects);
    if (sizeFieldset && !isSizeChosen(sizeFieldset)) return; // guard blocks anyway until a size is chosen
    const values = Array.from(variantSelects.querySelectorAll('.product-form__input[data-option-handle]')).map((fieldset) => {
      const radio = fieldset.querySelector('input[type="radio"]:checked');
      if (radio) return radio.value;
      const select = fieldset.querySelector('select');
      return select ? select.value : null;
    });
    if (!values.length || values.some((value) => value == null || value === '')) return;
    const title = values.map((value) => String(value).trim()).join(' / ');
    document.querySelectorAll(`${FBT_SELECT}.${FBT_THIS_ITEM}`).forEach((select) => {
      const index = Array.from(select.options).findIndex((option) => option.textContent.trim() === title);
      if (index < 0 || select.selectedIndex === index) return;
      select.selectedIndex = index;
      select.dispatchEvent(new Event('change', { bubbles: true })); // valid value: lets the widget update its totals
    });
    // A main-size pick also clears a "this item" outline that is showing.
    document.querySelectorAll(FBT_CONTAINER).forEach((container) => {
      const message = container.querySelector('.size-lock-fbt-error');
      if (message && !message.hidden) fbtMissing(container);
    });
  };

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;
      const variantSelects = input.closest('variant-selects');
      if (!variantSelects) return;
      const sectionId = getSectionId(variantSelects);
      if (sectionId) setTimeout(() => syncFbtThisItem(sectionId), 0);
    },
    true
  );

  window.addEventListener('pageshow', () => initFbt(document));
  onReady(() => {
    initFbt(document);
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.(FBT_CONTAINER) || node.matches?.(FBT_SELECT) || node.querySelector?.(`${FBT_CONTAINER}, ${FBT_SELECT}`)) {
            initFbt(node.closest(FBT_CONTAINER) || node);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  });

  onReady(() => initSizeLock(document));
  window.addEventListener('pageshow', () => initSizeLock(document));
  document.addEventListener('product-info:loaded', (event) => initSizeLock(event.target || document));
  document.addEventListener('shopify:section:load', (event) => initSizeLock(event.target || document));

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;

      const variantSelects = input.closest('variant-selects');
      if (!variantSelects) return;

      const sectionId = getSectionId(variantSelects);
      const fieldset = input.closest('.product-form__input[data-option-handle]');
      const handle = fieldset?.dataset.optionHandle;

      if (input instanceof HTMLInputElement && input.type === 'radio' && SIZE_HANDLES.has(handle)) {
        markSizeChosen(sectionId, input);
        return;
      }

      const state = getState(sectionId);
      if (!state.sizeTouched) {
        lockUntilSizeChoice(sectionId, variantSelects.closest('product-info') || document);
      }
    },
    true
  );

  // Guard at the WINDOW level in the capture phase, and on the button CLICK (which fires before
  // `submit`, including the implicit submit from pressing Enter in a field). Reason: Section
  // Store's cart drawer (block-cart.js) registers window.addEventListener('submit', ..., {capture:true})
  // and adds to cart itself; a document-level listener runs after it and never gets the chance to block.
  window.addEventListener(
    'submit',
    (event) => blockSubmitIfSizeMissing(event, event.target),
    true
  );
  window.addEventListener(
    'click',
    (event) => {
      const button = event.target?.closest?.('button[name="add"], .product-form__submit');
      if (!button) return;
      const form = button.form || button.closest('form');
      blockSubmitIfSizeMissing(event, form);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target?.closest(
        '.shopify-payment-button__button, .shopify-payment-button__more-options'
      );
      if (!button) return;

      const productForm = button.closest('product-form');
      const sectionId = productForm?.dataset.sectionId;
      const sizeFieldset = findSizeFieldset(findVariantSelects(sectionId));
      if (!sizeFieldset || isSizeChosen(sizeFieldset)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      applySoftLock(sectionId, true, productForm);
      showError(productForm);
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const sectionId = data?.sectionId;
      if (!sectionId) return;
      const state = getState(sectionId);
      if (!state.sizeTouched) schedulePostRenderClear(sectionId, document);
    });
  }

  const observerRoot = document.querySelector('main') || document.body;
  if (observerRoot) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (
            node.matches?.('product-info') ||
            node.matches?.(VARIANT_SELECTS_SELECTOR) ||
            node.querySelector?.(`product-info, ${VARIANT_SELECTS_SELECTOR}`)
          ) {
            initSizeLock(node);
          }
        }
      }
    });

    observer.observe(observerRoot, { childList: true, subtree: true });
  }
})();
